import React, { useCallback, type PointerEvent as ReactPointerEvent } from 'react';
import clsx from 'clsx';
import type { CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { SelectionFrame } from './SelectionFrame';
import type { BoxGesture } from './useBoxPointer';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The room between the text and its frame, in CSS pixels (design b1: `inset: -8px -12px`). */
const FRAME_PAD_X = 12;
const FRAME_PAD_Y = 6;

export interface StageBoxSize {
  width: number;
  height: number;
}

interface TextBoxItemProps {
  box: CoverTextBox;
  side: number;
  size: StageBoxSize;
  isSelected: boolean;
  isHidden: boolean;
  onStart: (event: ReactPointerEvent<HTMLElement>, box: CoverTextBox, gesture: BoxGesture) => void;
  onMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onEnd: (event: ReactPointerEvent<HTMLElement>) => void;
  onEdit: (id: string) => void;
}

const TextBoxItemComponent: React.FC<TextBoxItemProps> = (props) => {
  const { box, side, size, isSelected, isHidden, onStart, onMove, onEnd, onEdit } = props;
  const startMove = useCallback((event: ReactPointerEvent<HTMLElement>) => onStart(event, box, 'move'), [box, onStart]);
  const edit = useCallback(() => onEdit(box.id), [box.id, onEdit]);
  if (isHidden) {
    return null;
  }
  return (
    <div
      className={clsx(styles.textHit, isSelected && styles.textHitOn)}
      style={{
        left: box.x * side,
        top: box.y * side,
        width: size.width + FRAME_PAD_X * 2,
        height: size.height + FRAME_PAD_Y * 2,
        transform: `translate(-50%, -50%) rotate(${box.rotationDeg}deg)`,
      }}
      role="button"
      tabIndex={-1}
      aria-label={box.text}
      aria-pressed={isSelected}
      onPointerDown={startMove}
      onPointerMove={onMove}
      onPointerUp={onEnd}
      onPointerCancel={onEnd}
      onDoubleClick={edit}
    >
      {isSelected && <SelectionFrame box={box} onStart={onStart} onMove={onMove} onEnd={onEnd} />}
    </div>
  );
};

const TextBoxItem = TypedMemo(TextBoxItemComponent);

interface TextBoxLayerProps {
  texts: CoverTextBox[];
  side: number;
  sizes: Record<string, StageBoxSize>;
  selectedId: string | null;
  editingId: string | null;
  onStart: (event: ReactPointerEvent<HTMLElement>, box: CoverTextBox, gesture: BoxGesture) => void;
  onMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onEnd: (event: ReactPointerEvent<HTMLElement>) => void;
  onEdit: (id: string) => void;
}

/**
 * The text boxes of the stage as elements over the canvas: each one takes the pointer (select,
 * drag, double-click to type), and the selected one shows its frame. The canvas draws the text;
 * these elements are clear.
 */
const TextBoxLayerComponent: React.FC<TextBoxLayerProps> = (props) => {
  const { texts, side, sizes, selectedId, editingId, onStart, onMove, onEnd, onEdit } = props;
  return (
    <>
      {texts.map((box) => (
        <TextBoxItem
          key={box.id}
          box={box}
          side={side}
          size={sizes[box.id] ?? { width: 0, height: 0 }}
          isSelected={box.id === selectedId}
          isHidden={box.id === editingId}
          onStart={onStart}
          onMove={onMove}
          onEnd={onEnd}
          onEdit={onEdit}
        />
      ))}
    </>
  );
};

export const TextBoxLayer = TypedMemo(TextBoxLayerComponent);
